import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subject, Subscription } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';
import { SchemaField, SystemFieldType, DocumentSchema, FieldDataType } from 'src/app/modules/core/store/models/document.model';
import {
  FilterGroup,
  Filter,
  RuleOperator,
  DateFilterOptions,
  ProximityFilterSettings
} from 'src/app/modules/core/store/models/filters.model';
import { Workflow, WorkflowStep } from 'src/app/modules/core/store/models/workflow.model';
import { GlobalEventListenerService } from '../../../services/global-event-listener.service';
import { GeofenceService } from 'src/app/modules/core/services/geofence.service';
import { Dataset } from 'src/app/modules/core/store/models/dataset.model';
import { Document } from 'src/app/modules/core/store/models/document.model';
import { PicklistService } from '../../../services/picklist/picklist.service';
import { UserProfileService } from '@app/modules/core/services/user-profile.service';

@Component({
    selector: 'topo-filter-item',
    templateUrl: './filter-item.component.html',
    styleUrls: ['./filter-item.component.scss'],
    standalone: false
})
export class FilterItemComponent implements OnInit, OnDestroy {
  @Input() filterGroup: FilterGroup;
  @Input() set filter(value: Filter) {
    this._filter = value;
    if (this._filter?.operator === RuleOperator.In || this._filter?.operator === RuleOperator.NotIn) {
      this.inFilterItems = this.getInFilterItems();
    }
  }
  get filter(): Filter {
    return this._filter;
  }
  private _filter: Filter;
  @Input() schema: DocumentSchema;
  @Input() workflow: Workflow;
  @Input() unitsConversionValue: number;
  @Input() lookupTargetSchemaFields: SchemaField[];
  @Output() enableSaveFilterbuttonEvent = new EventEmitter<Boolean>();
  @Input() useForDatamart: boolean;
  public SchemaDataType = FieldDataType;
  public RuleOperator = RuleOperator;
  public DateFilterOptions = DateFilterOptions;
  schemaFields: SchemaField[];
  operators: { name: string; value: RuleOperator }[];
  filterDataType: FieldDataType;
  operatorDisplayValues: any;
  picklistValues = {};
  enumerationValues = {};
  businessUnitValues = {};
  hasBusinessUnits: boolean;
  selectedEnumerationValue = {};
  selectedBusinessUnitValue = {};
  isGenericFilter: boolean;
  isWorkflowFilter: boolean;
  isDateTimeFilter: boolean;
  isBooleanFilter: boolean;
  isEnumFilter: boolean;
  isBusinessUnitsFilter: boolean;
  isDistanceFilter: boolean;
  isPicklistFilter: boolean;
  isDurationFilter: boolean;
  isInFilter: boolean;
  isGeofenceFilter: boolean;
  isProximityFilter: boolean;
  lastFilterValue: any;
  showSelection: boolean;
  showSelectionModal: boolean;
  showProximitySettings: boolean;
  proximitySettingsDisplay: string;
  proximitySettings: ProximityFilterSettings;
  destroy$ = new Subject<void>();
  clickSubscription: Subscription = null;
  inFilterText: string;
  geofenceDatasets: Dataset[];
  geofenceDatasetUId: string;
  geofenceDocuments: Document[];
  geofenceDocumentUId: string;
  personTypeDatasets: Dataset[];
  personTypeDatasetUId: string;
  personTypeDocuments: Document[];
  personTypeDocumentUId: string;
  datasetChanged: boolean;
  inFilterItems: any[];
  nonGeoFenceDataSelected: boolean;
  constructor(
    private translate: TranslateService,
    private eventListenerService: GlobalEventListenerService,
    private geofenceService: GeofenceService,
    private picklistService: PicklistService,
    private userProfileService: UserProfileService,
    private changeDetector: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.populateOperatorDisplayValues();
    this.schemaFields = this.getSchemaFields(this.schema?.fields);
    this.operators = this.getOperators(this.filter);
    this.filterDataType = this.getSelectedDataType(this.filter);
    this.getPicklistValues(this.schema);
    this.getEnumerationValues(this.schema);
    this.getBusinessUnitsValues(this.schema);
    this.setFilterTypeFlags();

    //this.getPersonTypeValues(this.filter);
    this.geofenceService.geofenceDatasets$.pipe(takeUntil(this.destroy$)).subscribe(ds => {
      this.geofenceDatasets = ds;
      this.changeDetector.markForCheck();
    });

    this.getGeofenceValues(this.filter);
    this.geofenceService.geofenceDatasets$.pipe(takeUntil(this.destroy$)).subscribe(ds => {
      this.geofenceDatasets = ds;
      this.changeDetector.markForCheck();
    });

    if (!this.geofenceService.isInitialized) {
      this.geofenceService.getGeofenceDatasets();
    }
    if (
      this.filterGroup.filters.find(
        x =>
          (x.operator == RuleOperator.InASingleGeofence || x.operator == RuleOperator.NotInASingleGeofence) &&
          x.value?.split('||').length == 6 &&
          !Boolean(x.value?.split('||').at(5))
      )
    ) {
      this.enableSaveFilterbuttonEvent.emit(true);
    }
  }

  populateOperatorDisplayValues() {
    this.operatorDisplayValues = {
      [RuleOperator.Equal]: this.translate.instant(`FILTERS.OPERATOR-${RuleOperator.Equal}`),
      [RuleOperator.NotEqual]: this.translate.instant(`FILTERS.OPERATOR-${RuleOperator.NotEqual}`),
      [RuleOperator.GreaterThan]: this.translate.instant(`FILTERS.OPERATOR-${RuleOperator.GreaterThan}`),
      [RuleOperator.GreaterThanEqualTo]: this.translate.instant(`FILTERS.OPERATOR-${RuleOperator.GreaterThanEqualTo}`),
      [RuleOperator.LessThan]: this.translate.instant(`FILTERS.OPERATOR-${RuleOperator.LessThan}`),
      [RuleOperator.LessThanEqualTo]: this.translate.instant(`FILTERS.OPERATOR-${RuleOperator.LessThanEqualTo}`),
      [RuleOperator.Contains]: this.translate.instant(`FILTERS.OPERATOR-${RuleOperator.Contains}`),
      [RuleOperator.Proximity]: this.translate.instant(`FILTERS.OPERATOR-${RuleOperator.Proximity}`),
      [RuleOperator.DateIsWithinLast]: this.translate.instant(`FILTERS.OPERATOR-${RuleOperator.DateIsWithinLast}`),
      [RuleOperator.DoesNotContain]: this.translate.instant(`FILTERS.OPERATOR-${RuleOperator.DoesNotContain}`),
      [RuleOperator.DateIsNotWithinLast]: this.translate.instant(`FILTERS.OPERATOR-${RuleOperator.DateIsNotWithinLast}`),
      [RuleOperator.IsEmpty]: this.translate.instant(`FILTERS.OPERATOR-${RuleOperator.IsEmpty}`),
      [RuleOperator.IsNotEmpty]: this.translate.instant(`FILTERS.OPERATOR-${RuleOperator.IsNotEmpty}`),
      [RuleOperator.In]: this.translate.instant(`FILTERS.OPERATOR-${RuleOperator.In}`),
      [RuleOperator.NotIn]: this.translate.instant(`FILTERS.OPERATOR-${RuleOperator.NotIn}`),
      [RuleOperator.DateIsWithinNext]: this.translate.instant(`FILTERS.OPERATOR-${RuleOperator.DateIsWithinNext}`),
      [RuleOperator.DateIsNotWithinNext]: this.translate.instant(`FILTERS.OPERATOR-${RuleOperator.DateIsNotWithinNext}`),
      [RuleOperator.InASingleGeofence]: this.translate.instant(`FILTERS.OPERATOR-${RuleOperator.InASingleGeofence}`),
      [RuleOperator.NotInASingleGeofence]: this.translate.instant(`FILTERS.OPERATOR-${RuleOperator.NotInASingleGeofence}`),
      [RuleOperator.InProximity]: this.translate.instant(`FILTERS.OPERATOR-${RuleOperator.InProximity}`),
      [RuleOperator.NotInProximity]: this.translate.instant(`FILTERS.OPERATOR-${RuleOperator.NotInProximity}`),
      [RuleOperator.Intersects]: this.translate.instant(`FILTERS.OPERATOR-${RuleOperator.Intersects}`),
      [RuleOperator.EqualNoCase]: this.translate.instant(`FILTERS.OPERATOR-${RuleOperator.EqualNoCase}`),
      [RuleOperator.NotEqualNoCase]: this.translate.instant(`FILTERS.OPERATOR-${RuleOperator.NotEqualNoCase}`)
    };
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onConfirmField(response: boolean) {
    this.showSelectionModal = response;
  }

  selectedFieldResponse(response: string) {
    this.filter.value = '{{ ' + response + ' }}';
  }

  setFilterTypeFlags() {
    this.isGenericFilter = false;
    this.isWorkflowFilter = false;
    this.isDateTimeFilter = false;
    this.isBooleanFilter = false;
    this.isEnumFilter = false;
    this.isBusinessUnitsFilter = false;
    this.isDistanceFilter = false;
    this.isPicklistFilter = false;
    this.isDurationFilter = false;
    this.isInFilter = false;
    this.isGeofenceFilter = false;
    this.isProximityFilter = false;

    if (
      this.filterDataType === FieldDataType.Text &&
      (this.filter.operator === RuleOperator.In || this.filter.operator === RuleOperator.NotIn)
    ) {
      this.isInFilter = true;
      return;
    }

    if (
      this.filterDataType == FieldDataType.Geometry &&
      (this.filter.operator == RuleOperator.InASingleGeofence || this.filter.operator == RuleOperator.NotInASingleGeofence)
    ) {
      this.populateGeofenceDocuments();
      this.isGeofenceFilter = true;
      return;
    }

    if (
      this.filterDataType == FieldDataType.Geometry &&
      (this.filter.operator == RuleOperator.InProximity || this.filter.operator == RuleOperator.NotInProximity)
    ) {
      this.isProximityFilter = true;
      this.proximitySettingsDisplay = JSON.parse(this.filter?.value)?.display;
      if (!!!this.proximitySettingsDisplay) {
        this.proximitySettingsDisplay = this.translate.instant('FILTERS.PROXIMITY-FILTER');
      }
      return;
    }

    if (
      this.filterDataType !== FieldDataType.UId &&
      this.filterDataType !== FieldDataType.DateTime &&
      this.filterDataType !== FieldDataType.Distance &&
      this.filterDataType !== FieldDataType.Picklist &&
      this.filterDataType !== FieldDataType.Duration &&
      this.filterDataType !== FieldDataType.Enumeration &&
      this.filterDataType !== FieldDataType.BusinessUnits &&
      this.filterDataType !== FieldDataType.Boolean &&
      this.filter.operator !== RuleOperator.IsEmpty &&
      this.filter.operator !== RuleOperator.IsNotEmpty
    ) {
      this.isGenericFilter = true;
      return;
    }

    if (this.filterDataType === FieldDataType.UId) {
      this.isWorkflowFilter = true;
      return;
    }

    if (this.filterDataType === FieldDataType.DateTime) {
      this.isDateTimeFilter = true;
      return;
    }

    if (this.filterDataType === FieldDataType.Boolean) {
      this.isBooleanFilter = true;
      return;
    }

    if (
      this.filterDataType === FieldDataType.Enumeration &&
      (this.filter.operator === RuleOperator.In || this.filter.operator === RuleOperator.NotIn)
    ) {
      this.isEnumFilter = true;
      return;
    }

    if (
      this.filterDataType === FieldDataType.BusinessUnits &&
      (this.filter.operator === RuleOperator.In || this.filter.operator === RuleOperator.NotIn)
    ) {
      this.isBusinessUnitsFilter = true;
      return;
    }

    if (this.filterDataType === FieldDataType.Distance) {
      this.initializeDistanceDisplayValue(this.filter);
      this.isDistanceFilter = true;
      return;
    }

    if (
      this.filterDataType === FieldDataType.Picklist &&
      (this.filter.operator === RuleOperator.Equal || this.filter.operator === RuleOperator.NotEqual)
    ) {
      this.isPicklistFilter = true;
    }

    if (this.filterDataType === FieldDataType.Duration) {
      this.isDurationFilter = true;
    }
    if (!this.isGeofenceFilter && this.nonGeoFenceDataSelected) {
      this.enableSaveFilterbuttonEvent.emit(true);
    }
  }

  getPicklistValues(schema: DocumentSchema): void {
    this.picklistValues = {};

    if (!!schema) {
      schema.fields?.map(f => {
        if (f.dataType === FieldDataType.Picklist) {
          if (f.settings?.staticList === true) {
            this.picklistValues[f.systemName] = f.picklistValues;
          } else {
            this.picklistService
              .getPicklistValues(f)
              .pipe(take(1))
              .subscribe(picklistValues => {
                this.picklistValues[f.systemName] = picklistValues;
              });
          }
        }
      });
    }
  }

  getEnumerationValues(schema: DocumentSchema): void {
    this.enumerationValues = {};

    if (!!schema) {
      schema.fields?.map(f => {
        if (f.dataType === FieldDataType.Enumeration) {
          this.enumerationValues[f.systemName] = f.enumerationValues;
        }
      });
    }
  }

  getBusinessUnitsValues(schema: DocumentSchema): void {
    this.hasBusinessUnits = false;
    this.businessUnitValues = {};

    const field = schema?.fields?.find(f => f.dataType === FieldDataType.BusinessUnits);
    if (!!field) {
      this.hasBusinessUnits = this.userProfileService.getUserHasBusinessUnits();
      if (this.hasBusinessUnits) {
        this.businessUnitValues[field.systemName] = this.userProfileService.getUserBusinessUnits();
        this.inFilterItems = this.getInFilterItems();
      }
      this.changeDetector.markForCheck();
    }
  }

  getSchemaFields(fields: SchemaField[]): SchemaField[] {
    if (!fields) {
      return [];
    }

    return fields
      .filter(f => {
        return (
          (f.fieldType & SystemFieldType.Hidden) !== SystemFieldType.Hidden ||
          f.systemName === 'external_id' ||
          f.systemName === 'workflow_step_uid' ||
          f.systemName === 'date_created' ||
          f.systemName === 'date_updated'
        );
      })
      .sort((f1, f2) => (f1.systemName > f2.systemName ? 1 : -1));
  }

  onFocus(elm: any, event: any): void {
    if (elm.classList.contains('is-active')) {
      elm.classList.remove('is-active');
      this.showSelection = false;

      this.unsubscribeToClicks();
    } else {
      elm.classList.add('is-active');
      this.showSelection = true;

      this.subscribeToClicks(elm);
    }
  }

  subscribeToClicks(elm: any) {
    if (this.clickSubscription) {
      return;
    }

    this.clickSubscription = this.eventListenerService.clickEvents$.pipe(takeUntil(this.destroy$)).subscribe(target => {
      if (!elm.contains(target)) {
        elm.classList.remove('is-active');
        this.showSelection = true;

        this.unsubscribeToClicks();
      }
    });
  }

  unsubscribeToClicks() {
    if (this.clickSubscription) {
      this.showSelection = false;
      this.clickSubscription.unsubscribe();
      this.clickSubscription = null;
    }
  }

  openProximitySettings(): void {
    this.proximitySettings = !!this.filter?.value ? JSON.parse(this.filter?.value) : null;
    this.showProximitySettings = true;
  }

  proximitySettingsChanged(settings: ProximityFilterSettings): void {
    this.filter.value = JSON.stringify(settings);
    this.proximitySettingsDisplay = JSON.parse(this.filter?.value)?.display;
    if (!!!this.proximitySettingsDisplay) {
      this.proximitySettingsDisplay = this.translate.instant('FILTERS.PROXIMITY-FILTER');
    }
    this.showProximitySettings = false;
  }

  closeProximitySettings(): void {
    this.showProximitySettings = false;
  }

  selectDsField() {
    this.showSelection = false;
    this.showSelectionModal = true;
  }

  getOperators(filter: Filter): { name: string; value: RuleOperator }[] {
    if (!filter) {
      return [];
    }

    let operators: { name: string; value: RuleOperator }[] = [];

    if (!this.schema) {
      operators = this.getDefaultOperators();
      return operators;
    }

    const field = this.schema.fields.find(f => f.systemName === filter.field);
    if (!field) {
      operators = this.getDefaultOperators();
      return operators;
    }

    switch (field.dataType) {
      case FieldDataType.Color:
        operators = this.getColorOperators();
        break;

      case FieldDataType.DateTime:
        operators = this.getDateTimeOperators();
        break;

      case FieldDataType.Distance:
        operators = this.getDistanceOperators();
        break;

      case FieldDataType.Email:
        operators = this.getEmailOperators();
        break;

      case FieldDataType.Geometry:
        operators = this.getGeometryOperators();
        break;

      case FieldDataType.Boolean:
        operators = this.getBooleanOperators();
        break;

      case FieldDataType.Enumeration:
        operators = this.getEnumOperators();
        break;

      case FieldDataType.BusinessUnits:
        operators = this.getBusinessUnitsOperators();
        break;

      case FieldDataType.Image:
        operators = this.getImageOperators();
        break;

      case FieldDataType.MultiPicklist:
        operators = this.getMultiPicklistOperators();
        break;

      case FieldDataType.Number:
        operators = this.getNumberOperators();
        break;

      case FieldDataType.Phone:
        operators = this.getPhoneOperators();
        break;

      case FieldDataType.Picklist:
        operators = this.getPicklistOperators();
        break;

      case FieldDataType.Text:
        operators = this.getTextOperators();
        break;

      case FieldDataType.User:
        operators = this.getUserOperators();
        break;

      case FieldDataType.SingleReference:
        operators = this.getSingleReferenceOperators();
        break;

      case FieldDataType.UId:
        operators = this.getUIdOperators();
        break;

      case FieldDataType.Url:
        operators = this.getUrlOperators();
        break;

      case FieldDataType.Array:
        operators = this.getArrayOperators();
        break;

      case FieldDataType.Duration:
        operators = this.getNumberOperators();
        break;
    }

    const existingFilter = operators.find(o => o.value === this.filter?.operator);
    if (!existingFilter) {
      this.filter.operator = operators[0].value;
    }

    return operators;
  }

  getDefaultOperators() {
    const operators: { name: string; value: RuleOperator }[] = [
      { name: this.operatorDisplayValues[RuleOperator.Equal], value: RuleOperator.Equal },
      { name: this.operatorDisplayValues[RuleOperator.NotEqual], value: RuleOperator.NotEqual },
      { name: this.operatorDisplayValues[RuleOperator.GreaterThan], value: RuleOperator.GreaterThan },
      { name: this.operatorDisplayValues[RuleOperator.GreaterThanEqualTo], value: RuleOperator.GreaterThanEqualTo },
      { name: this.operatorDisplayValues[RuleOperator.LessThan], value: RuleOperator.LessThan },
      { name: this.operatorDisplayValues[RuleOperator.LessThanEqualTo], value: RuleOperator.LessThanEqualTo },
      { name: this.operatorDisplayValues[RuleOperator.IsEmpty], value: RuleOperator.IsEmpty },
      { name: this.operatorDisplayValues[RuleOperator.IsNotEmpty], value: RuleOperator.IsNotEmpty }
    ];

    return operators;
  }

  getColorOperators() {
    const operators: { name: string; value: RuleOperator }[] = [
      { name: this.operatorDisplayValues[RuleOperator.Equal], value: RuleOperator.Equal },
      { name: this.operatorDisplayValues[RuleOperator.NotEqual], value: RuleOperator.NotEqual },
      { name: this.operatorDisplayValues[RuleOperator.IsEmpty], value: RuleOperator.IsEmpty },
      { name: this.operatorDisplayValues[RuleOperator.IsNotEmpty], value: RuleOperator.IsNotEmpty }
    ];

    return operators;
  }

  getDateTimeOperators() {
    const operators: { name: string; value: RuleOperator }[] = [
      { name: this.operatorDisplayValues[RuleOperator.DateIsWithinLast], value: RuleOperator.DateIsWithinLast },
      { name: this.operatorDisplayValues[RuleOperator.DateIsNotWithinLast], value: RuleOperator.DateIsNotWithinLast },
      { name: this.operatorDisplayValues[RuleOperator.Equal], value: RuleOperator.Equal },
      { name: this.operatorDisplayValues[RuleOperator.NotEqual], value: RuleOperator.NotEqual },
      { name: this.operatorDisplayValues[RuleOperator.GreaterThan], value: RuleOperator.GreaterThan },
      { name: this.operatorDisplayValues[RuleOperator.GreaterThanEqualTo], value: RuleOperator.GreaterThanEqualTo },
      { name: this.operatorDisplayValues[RuleOperator.LessThan], value: RuleOperator.LessThan },
      { name: this.operatorDisplayValues[RuleOperator.LessThanEqualTo], value: RuleOperator.LessThanEqualTo },
      { name: this.operatorDisplayValues[RuleOperator.IsEmpty], value: RuleOperator.IsEmpty },
      { name: this.operatorDisplayValues[RuleOperator.IsNotEmpty], value: RuleOperator.IsNotEmpty },
      { name: this.operatorDisplayValues[RuleOperator.DateIsWithinNext], value: RuleOperator.DateIsWithinNext },
      { name: this.operatorDisplayValues[RuleOperator.DateIsNotWithinNext], value: RuleOperator.DateIsNotWithinNext }
    ];

    return operators;
  }

  getDistanceOperators() {
    const operators: { name: string; value: RuleOperator }[] = [
      { name: this.operatorDisplayValues[RuleOperator.Equal], value: RuleOperator.Equal },
      { name: this.operatorDisplayValues[RuleOperator.NotEqual], value: RuleOperator.NotEqual },
      { name: this.operatorDisplayValues[RuleOperator.GreaterThan], value: RuleOperator.GreaterThan },
      { name: this.operatorDisplayValues[RuleOperator.GreaterThanEqualTo], value: RuleOperator.GreaterThanEqualTo },
      { name: this.operatorDisplayValues[RuleOperator.LessThan], value: RuleOperator.LessThan },
      { name: this.operatorDisplayValues[RuleOperator.LessThanEqualTo], value: RuleOperator.LessThanEqualTo },
      { name: this.operatorDisplayValues[RuleOperator.IsEmpty], value: RuleOperator.IsEmpty },
      { name: this.operatorDisplayValues[RuleOperator.IsNotEmpty], value: RuleOperator.IsNotEmpty }
    ];

    return operators;
  }

  getGeometryOperators() {
    const operators: { name: string; value: RuleOperator }[] = [
      { name: this.operatorDisplayValues[RuleOperator.Intersects], value: RuleOperator.Intersects },
      { name: this.operatorDisplayValues[RuleOperator.IsEmpty], value: RuleOperator.IsEmpty },
      { name: this.operatorDisplayValues[RuleOperator.IsNotEmpty], value: RuleOperator.IsNotEmpty },
      { name: this.operatorDisplayValues[RuleOperator.InASingleGeofence], value: RuleOperator.InASingleGeofence },
      { name: this.operatorDisplayValues[RuleOperator.NotInASingleGeofence], value: RuleOperator.NotInASingleGeofence },
      { name: this.operatorDisplayValues[RuleOperator.InProximity], value: RuleOperator.InProximity },
      { name: this.operatorDisplayValues[RuleOperator.NotInProximity], value: RuleOperator.NotInProximity }
    ];

    return operators;
  }

  getEnumOperators() {
    const operators: { name: string; value: RuleOperator }[] = [
      { name: this.operatorDisplayValues[RuleOperator.In], value: RuleOperator.In },
      { name: this.operatorDisplayValues[RuleOperator.NotIn], value: RuleOperator.NotIn },
      { name: this.operatorDisplayValues[RuleOperator.IsEmpty], value: RuleOperator.IsEmpty },
      { name: this.operatorDisplayValues[RuleOperator.IsNotEmpty], value: RuleOperator.IsNotEmpty }
    ];

    return operators;
  }

  getBusinessUnitsOperators() {
    const operators: { name: string; value: RuleOperator }[] = [
      { name: this.operatorDisplayValues[RuleOperator.In], value: RuleOperator.In },
      { name: this.operatorDisplayValues[RuleOperator.NotIn], value: RuleOperator.NotIn }
    ];

    return operators;
  }

  getBooleanOperators() {
    const operators: { name: string; value: RuleOperator }[] = [
      { name: this.operatorDisplayValues[RuleOperator.Equal], value: RuleOperator.Equal }
    ];

    return operators;
  }

  getImageOperators() {
    const operators: { name: string; value: RuleOperator }[] = [
      { name: this.operatorDisplayValues[RuleOperator.Equal], value: RuleOperator.Equal },
      { name: this.operatorDisplayValues[RuleOperator.NotEqual], value: RuleOperator.NotEqual },
      { name: this.operatorDisplayValues[RuleOperator.Contains], value: RuleOperator.Contains },
      { name: this.operatorDisplayValues[RuleOperator.DoesNotContain], value: RuleOperator.DoesNotContain }
    ];

    return operators;
  }

  getMultiPicklistOperators() {
    const operators: { name: string; value: RuleOperator }[] = [
      { name: this.operatorDisplayValues[RuleOperator.Contains], value: RuleOperator.Contains },
      { name: this.operatorDisplayValues[RuleOperator.DoesNotContain], value: RuleOperator.DoesNotContain }
    ];

    return operators;
  }

  getNumberOperators() {
    const operators: { name: string; value: RuleOperator }[] = [
      { name: this.operatorDisplayValues[RuleOperator.Equal], value: RuleOperator.Equal },
      { name: this.operatorDisplayValues[RuleOperator.NotEqual], value: RuleOperator.NotEqual },
      { name: this.operatorDisplayValues[RuleOperator.GreaterThan], value: RuleOperator.GreaterThan },
      { name: this.operatorDisplayValues[RuleOperator.GreaterThanEqualTo], value: RuleOperator.GreaterThanEqualTo },
      { name: this.operatorDisplayValues[RuleOperator.LessThan], value: RuleOperator.LessThan },
      { name: this.operatorDisplayValues[RuleOperator.LessThanEqualTo], value: RuleOperator.LessThanEqualTo },
      { name: this.operatorDisplayValues[RuleOperator.IsEmpty], value: RuleOperator.IsEmpty },
      { name: this.operatorDisplayValues[RuleOperator.IsNotEmpty], value: RuleOperator.IsNotEmpty }
    ];

    return operators;
  }

  getPicklistOperators() {
    const operators: { name: string; value: RuleOperator }[] = [
      { name: this.operatorDisplayValues[RuleOperator.Equal], value: RuleOperator.Equal },
      { name: this.operatorDisplayValues[RuleOperator.NotEqual], value: RuleOperator.NotEqual },
      { name: this.operatorDisplayValues[RuleOperator.IsEmpty], value: RuleOperator.IsEmpty },
      { name: this.operatorDisplayValues[RuleOperator.IsNotEmpty], value: RuleOperator.IsNotEmpty }
    ];

    return operators;
  }

  getTextOperators() {
    const operators: { name: string; value: RuleOperator }[] = [
      { name: this.operatorDisplayValues[RuleOperator.Equal], value: RuleOperator.Equal },
      { name: this.operatorDisplayValues[RuleOperator.NotEqual], value: RuleOperator.NotEqual },
      { name: this.operatorDisplayValues[RuleOperator.EqualNoCase], value: RuleOperator.EqualNoCase },
      { name: this.operatorDisplayValues[RuleOperator.NotEqualNoCase], value: RuleOperator.NotEqualNoCase },
      { name: this.operatorDisplayValues[RuleOperator.Contains], value: RuleOperator.Contains },
      { name: this.operatorDisplayValues[RuleOperator.DoesNotContain], value: RuleOperator.DoesNotContain },
      { name: this.operatorDisplayValues[RuleOperator.In], value: RuleOperator.In },
      { name: this.operatorDisplayValues[RuleOperator.NotIn], value: RuleOperator.NotIn },
      { name: this.operatorDisplayValues[RuleOperator.IsEmpty], value: RuleOperator.IsEmpty },
      { name: this.operatorDisplayValues[RuleOperator.IsNotEmpty], value: RuleOperator.IsNotEmpty }
    ];

    return operators;
  }

  getEmailOperators() {
    const operators: { name: string; value: RuleOperator }[] = [
      { name: this.operatorDisplayValues[RuleOperator.Equal], value: RuleOperator.Equal },
      { name: this.operatorDisplayValues[RuleOperator.NotEqual], value: RuleOperator.NotEqual },
      { name: this.operatorDisplayValues[RuleOperator.EqualNoCase], value: RuleOperator.EqualNoCase },
      { name: this.operatorDisplayValues[RuleOperator.NotEqualNoCase], value: RuleOperator.NotEqualNoCase },
      { name: this.operatorDisplayValues[RuleOperator.Contains], value: RuleOperator.Contains },
      { name: this.operatorDisplayValues[RuleOperator.DoesNotContain], value: RuleOperator.DoesNotContain },
      { name: this.operatorDisplayValues[RuleOperator.IsEmpty], value: RuleOperator.IsEmpty },
      { name: this.operatorDisplayValues[RuleOperator.IsNotEmpty], value: RuleOperator.IsNotEmpty }
    ];

    return operators;
  }

  getPhoneOperators() {
    const operators: { name: string; value: RuleOperator }[] = [
      { name: this.operatorDisplayValues[RuleOperator.Equal], value: RuleOperator.Equal },
      { name: this.operatorDisplayValues[RuleOperator.NotEqual], value: RuleOperator.NotEqual },
      { name: this.operatorDisplayValues[RuleOperator.Contains], value: RuleOperator.Contains },
      { name: this.operatorDisplayValues[RuleOperator.DoesNotContain], value: RuleOperator.DoesNotContain },
      { name: this.operatorDisplayValues[RuleOperator.IsEmpty], value: RuleOperator.IsEmpty },
      { name: this.operatorDisplayValues[RuleOperator.IsNotEmpty], value: RuleOperator.IsNotEmpty }
    ];

    return operators;
  }

  getUIdOperators() {
    const operators: { name: string; value: RuleOperator }[] = [
      { name: this.operatorDisplayValues[RuleOperator.Equal], value: RuleOperator.Equal },
      { name: this.operatorDisplayValues[RuleOperator.NotEqual], value: RuleOperator.NotEqual }
    ];

    return operators;
  }

  getUrlOperators() {
    const operators: { name: string; value: RuleOperator }[] = [
      { name: this.operatorDisplayValues[RuleOperator.Equal], value: RuleOperator.Equal },
      { name: this.operatorDisplayValues[RuleOperator.NotEqual], value: RuleOperator.NotEqual },
      { name: this.operatorDisplayValues[RuleOperator.Contains], value: RuleOperator.Contains },
      { name: this.operatorDisplayValues[RuleOperator.DoesNotContain], value: RuleOperator.DoesNotContain },
      { name: this.operatorDisplayValues[RuleOperator.IsEmpty], value: RuleOperator.IsEmpty },
      { name: this.operatorDisplayValues[RuleOperator.IsNotEmpty], value: RuleOperator.IsNotEmpty }
    ];

    return operators;
  }

  getUserOperators() {
    const operators: { name: string; value: RuleOperator }[] = [
      { name: this.operatorDisplayValues[RuleOperator.Equal], value: RuleOperator.Equal },
      { name: this.operatorDisplayValues[RuleOperator.NotEqual], value: RuleOperator.NotEqual },
      { name: this.operatorDisplayValues[RuleOperator.Contains], value: RuleOperator.Contains },
      { name: this.operatorDisplayValues[RuleOperator.DoesNotContain], value: RuleOperator.DoesNotContain },
      { name: this.operatorDisplayValues[RuleOperator.IsEmpty], value: RuleOperator.IsEmpty },
      { name: this.operatorDisplayValues[RuleOperator.IsNotEmpty], value: RuleOperator.IsNotEmpty }
    ];

    return operators;
  }

  getSingleReferenceOperators() {
    const operators: { name: string; value: RuleOperator }[] = [
      { name: this.operatorDisplayValues[RuleOperator.Equal], value: RuleOperator.Equal },
      { name: this.operatorDisplayValues[RuleOperator.NotEqual], value: RuleOperator.NotEqual },
      { name: this.operatorDisplayValues[RuleOperator.Contains], value: RuleOperator.Contains },
      { name: this.operatorDisplayValues[RuleOperator.DoesNotContain], value: RuleOperator.DoesNotContain },
      { name: this.operatorDisplayValues[RuleOperator.IsEmpty], value: RuleOperator.IsEmpty },
      { name: this.operatorDisplayValues[RuleOperator.IsNotEmpty], value: RuleOperator.IsNotEmpty }
    ];

    return operators;
  }

  getArrayOperators() {
    const operators: { name: string; value: RuleOperator }[] = [
      { name: this.operatorDisplayValues[RuleOperator.Contains], value: RuleOperator.Contains },
      { name: this.operatorDisplayValues[RuleOperator.DoesNotContain], value: RuleOperator.DoesNotContain }
    ];

    return operators;
  }

  getSelectedDataType(filter: Filter): FieldDataType {
    if (!filter || !this.schema) {
      return FieldDataType.Text;
    }

    const field = this.schema.fields.find(f => f.systemName === filter.field);
    return field?.dataType || FieldDataType.Text;
  }

  getWorkflowStepStatusName(filter: Filter): string {
    const step = this.workflow?.workflowSteps?.find(ws => ws.uId === filter.value);
    return step?.statusName;
  }

  getWorkflowStepStatusColorCode(filter: Filter): string {
    const step = this.workflow?.workflowSteps?.find(ws => ws.uId === filter.value);
    return step?.statusHtmlColorCode;
  }

  fieldTrack(index, item: SchemaField) {
    return item.uId;
  }

  fieldChange() {
    this.filter.value = null;
    this.filter.options = null;
    this.filterDataType = this.getSelectedDataType(this.filter);
    this.operators = this.getOperators(this.filter);
    this.setFilterTypeFlags();
    if (this.filter.operator === RuleOperator.In || this.filter.operator === RuleOperator.NotIn) {
      this.inFilterItems = this.getInFilterItems();
    }
  }

  operatorTrack(index, item: RuleOperator) {
    return item;
  }

  operatorChange() {
    let wasGenericFilter = this.isGenericFilter;
    this.setFilterTypeFlags();
    // When text value not used by operator
    // 1. Save away filter value
    // 2. Clear filter value
    if (wasGenericFilter && !this.isGenericFilter) {
      this.lastFilterValue = this.filter.value;
      this.filter.value = null;
    }
    // When text value once again used by operator
    // 1. Restore filter value
    if (!wasGenericFilter && this.isGenericFilter && !this.filter.value && this.lastFilterValue) {
      this.filter.value = this.lastFilterValue;
    }

    if (this.filter.operator === RuleOperator.In || this.filter.operator === RuleOperator.NotIn) {
      this.inFilterItems = this.getInFilterItems();
    }
  }

  updateDistanceValue(val: any, filter: Filter) {
    filter.value = (val * this.unitsConversionValue).toString();
  }

  workflowStepSelected(filter: Filter, workflowStep: WorkflowStep) {
    filter.value = workflowStep.uId;
  }

  initializeDistanceDisplayValue(filter: Filter): boolean {
    if (filter.value && !filter.displayValue) {
      filter.displayValue = filter.value / this.unitsConversionValue;
    }

    return true;
  }

  durationValueChanged(filter: Filter, value: number) {
    filter.value = value.toString();
  }

  deleteFilterClick(): void {
    const index = this.filterGroup.filters.indexOf(this.filter);
    this.filterGroup.filters.splice(index, 1);
    if (
      this.filterGroup.filters.find(
        x =>
          (x.operator == RuleOperator.InASingleGeofence || x.operator == RuleOperator.NotInASingleGeofence) &&
          x.value?.split('||').length == 6 &&
          x.value?.split('||')[5] == 'undefined'
      )
    ) {
      this.enableSaveFilterbuttonEvent.emit(true);
    } else {
      this.enableSaveFilterbuttonEvent.emit(false);
    }
  }

  addInFilterClick(): void {
    if (!this.inFilterText) {
      return;
    }

    this.filter.value = this.filter?.value?.length > 0 ? `${this.filter.value}||${this.inFilterText}` : this.inFilterText;
    this.inFilterText = '';
    this.inFilterItems = this.getInFilterItems();
    this.changeDetector.markForCheck();
  }

  onBusinessUnitsSelectionChanged(fieldName: string, selectedUIds: string[]): void {
    this.selectedBusinessUnitValue[fieldName] = selectedUIds;
  }

  addBusinessUnitFilterClick(): void {
    const selectedValue = this.selectedBusinessUnitValue[this.filter?.field];
    if (selectedValue === undefined || !selectedValue.length) {
      return;
    }

    this.filter.value = this.filter?.value?.length > 0 ? `${this.filter.value},${selectedValue[0]}` : selectedValue[0].toString();
    this.inFilterItems = this.getInFilterItems();
    this.changeDetector.markForCheck();
  }

  addEnumInFilterClick(): void {
    const selectedValue = this.selectedEnumerationValue[this.filter?.field];
    if (selectedValue === undefined) {
      return;
    }

    this.filter.value = this.filter?.value?.length > 0 ? `${this.filter.value},${selectedValue}` : selectedValue.toString();
    this.inFilterItems = this.getInFilterItems();
    this.changeDetector.markForCheck();
  }

  removeInFilterClick(itemToRemove: string): void {
    const field = this.schema.fields.find(f => f.systemName === this.filter.field);
    if (field?.dataType === FieldDataType.Enumeration || field?.dataType === FieldDataType.BusinessUnits) {
      const values = this.filter?.value?.split(',') ?? [];
      const idx = values.indexOf(itemToRemove);
      if (idx < 0) {
        return;
      }

      values.splice(idx, 1);
      this.filter.value = values.length > 0 ? values.join(',') : '';
    } else {
      const values = this.filter?.value?.split('||') ?? [];
      const idx = values.indexOf(itemToRemove);
      if (idx < 0) {
        return;
      }

      values.splice(idx, 1);

      this.filter.value = values.length > 0 ? values.join('||') : '';
    }
    this.inFilterItems = this.getInFilterItems();
    this.changeDetector.markForCheck();
  }

  private getInFilterItems(): any[] {
    const field = this.schema?.fields.find(f => f.systemName === this.filter.field);

    const values = [];
    if (field?.dataType === FieldDataType.Enumeration) {
      const ids = this.filter?.value?.toString()?.split(',') ?? [];
      field.enumerationValues
        ?.filter(v => ids.includes(v.value.toString()))
        .forEach(v => values.push({ id: v.value.toString(), name: v.name }));
    } else if (field?.dataType === FieldDataType.BusinessUnits) {
      const ids = this.filter?.value?.toString()?.split(',') ?? [];
      this.businessUnitValues?.[field.systemName]
        ?.filter(v => ids.includes(v.uId.toString()))
        .forEach(v => values.push({ id: v.uId, name: v.name }));
    } else {
      this.filter.value
        ?.split('||')
        ?.filter(v => v?.length > 0)
        .forEach(v => values.push({ id: v, name: v }));
    }
    return values;
  }

  geofenceDatasetOnChange(datasetUId): void {
    const dataset = this.geofenceDatasets.find(ds => ds.uId === datasetUId);
    this.setGeofenceFilterValue(
      dataset.organizationUId,
      dataset.uId,
      dataset.sourceOrgUId,
      dataset.sourceDatasetUId,
      dataset.systemDataTableName,
      this.geofenceDocumentUId
    );

    this.datasetChanged = true;
    this.populateGeofenceDocuments();
    this.changeDetector.markForCheck();
  }

  populateGeofenceDocuments(): void {
    this.nonGeoFenceDataSelected = false;
    const selectedValues = this.filter.value?.split('||')?.filter(v => v?.length > 0);
    if (!selectedValues || (selectedValues.length === 0 && !this.geofenceDatasets)) {
      this.nonGeoFenceDataSelected = true;
      this.enableSaveFilterbuttonEvent.emit(true);
      return;
    }

    this.geofenceService
      .getGeofenceDocuments(selectedValues[1])
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe(docs => {
        this.geofenceDocuments = docs;

        if (!!docs && docs.length > 0 && !!this.geofenceDatasets) {
          if (!this.geofenceDocumentUId || this.datasetChanged) {
            this.geofenceDocumentUId = docs[0].uid;
            this.datasetChanged = false;
            this.enableSaveFilterbuttonEvent.emit(false);
          }

          const dataset = this.geofenceDatasets.find(ds => ds.uId === this.geofenceDatasetUId);
          this.setGeofenceFilterValue(
            dataset.organizationUId,
            dataset.uId,
            dataset.sourceOrgUId,
            dataset.sourceDatasetUId,
            dataset.systemDataTableName,
            this.geofenceDocumentUId
          );
        } else {
          this.enableSaveFilterbuttonEvent.emit(true);
        }
        this.changeDetector.markForCheck();
      });
  }

  geofenceDocumentOnChange(documentUId): void {
    const dataset = this.geofenceDatasets.find(ds => ds.uId === this.geofenceDatasetUId);
    this.setGeofenceFilterValue(
      dataset.organizationUId,
      dataset.uId,
      dataset.sourceOrgUId,
      dataset.sourceDatasetUId,
      dataset.systemDataTableName,
      documentUId
    );
    this.changeDetector.markForCheck();
  }

  getGeofenceValues(filter: Filter): void {
    if (filter.operator === RuleOperator.InASingleGeofence || filter.operator === RuleOperator.NotInASingleGeofence) {
      const selectedValues = this.filter.value?.split('||')?.filter(v => v?.length > 0);
      if (!!selectedValues && selectedValues.length > 1) {
        this.geofenceDatasetUId = selectedValues[1];
      }

      if (!!selectedValues && selectedValues.length > 5) {
        this.geofenceDocumentUId = selectedValues[5];
      }
    }
    this.changeDetector.markForCheck();
  }

  setGeofenceFilterValue(
    organizationUId: string,
    datasetUId: string,
    sourceOrganizationUId: string,
    sourceDatasetUId: string,
    systemDataTableName: string,
    documentUId: string
  ) {
    this.filter.value = `${organizationUId}||${datasetUId}||${sourceOrganizationUId}||${sourceDatasetUId}||${systemDataTableName}||${documentUId}`;
  }
}
